import React, { useState } from 'react';

const HomePage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d460flne72egts1osolg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <img 
          src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/ded9fd3b52c9c34e230221365b9092d4.png"
          alt="Hero"
          className="w-full h-auto object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center px-2 sm:px-4">
          <div className="max-w-7xl w-full mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 border border-white/20 shadow-2xl mx-2">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
                  <span className="inline-block bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent drop-shadow-lg" style={{ textShadow: '0 0 30px rgba(255,200,0,0.5)' }}>
                    「どんぶり勘定」からの脱却。
                  </span>
                </h1>
                <div className="w-16 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mb-4 sm:mb-6 md:mb-8"></div>
                <p className="text-sm sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-3 sm:mb-4 md:mb-6 leading-relaxed tracking-wide">
                  数値を可視化し、根拠ある経営判断を導く
                </p>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-black mb-4 sm:mb-6 md:mb-8 leading-tight">
                  <span className="bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-300 bg-clip-text text-transparent animate-pulse">
                    御社専任の「社外CFO(最高財務責任者)」
                  </span>
                </p>
                <div className="bg-white/10 rounded-xl p-3 sm:p-4 md:p-6 border border-white/30 mb-4 sm:mb-6 md:mb-8">
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold text-white leading-relaxed">
                    <span className="text-yellow-300 font-bold">「最新のAI財務分析」</span>×<span className="text-cyan-300 font-bold">「支援歴25年の専門家視点」</span><br />
                    <span className="text-white/90">数字の整理はテクノロジーに任せ、社長は「未来の決断」に集中してください。</span>
                  </p>
                </div>
                <div>
                  <a 
                    href="/start-now" 
                    className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-4 md:py-5 rounded-full font-black text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl transition-all whitespace-nowrap cursor-pointer shadow-2xl hover:shadow-3xl transform hover:scale-110 border-4 border-white/30"
                  >
                    財務健康診断レポートを受け取る
                  </a>
                </div>
              </div>
              
              <div className="hidden lg:flex flex-col justify-center items-center space-y-4">
                <p className="text-xl md:text-2xl lg:text-3xl font-black text-white text-center bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent drop-shadow-lg px-4 py-2 rounded-lg" style={{ textShadow: '0 0 20px rgba(255,200,0,0.8)' }}>
                  社長、御社の数字を偏差値にしませんか?
                </p>
                <img 
                  src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/789816d82f24cd53765b52adb7c9dcc7.png"
                  alt="財務健康診断レポート"
                  className="w-full max-w-md rounded-lg shadow-2xl transform hover:scale-105 transition-transform"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultant Introduction Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            担当コンサルタント紹介
          </h2>
          
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 border-t-8 border-blue-500">
            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 items-start">
              <div className="lg:col-span-1 text-center">
                <img 
                  src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/6fdbf86114757741b36d6326dd9f6d3f.jpeg"
                  alt="経営財務コンサルタント　鹿田 知倫"
                  className="w-32 sm:w-40 md:w-48 h-42 sm:h-52 md:h-64 mx-auto rounded-lg shadow-lg object-cover mb-4 sm:mb-6"
                />
                <h4 className="text-xl sm:text-2xl md:text-3xl font-black text-blue-600 mb-2 sm:mb-4">鹿田 知倫</h4>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">（保有資格）</h3>
                <div className="text-sm sm:text-base md:text-lg text-gray-700 mb-4 space-y-1">
                  <p>税理行政書士/組織ｺﾝｻﾙﾀﾝﾄ</p>
                  <p>融資ｺﾝｻﾙﾀﾝﾄ/ｷｬｯｼｭﾌﾛｰｺｰﾁ</p>
                </div>
              </div>
              
              <div className="lg:col-span-2">
                <div className="space-y-4 sm:space-y-6 text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">
                  <p className="font-medium">
                    私はこれまで、税理士事務所とコンサルティング会社で<span className="font-bold text-blue-600">「お金」と「人」</span>の両面から中小企業経営を支援してきました。40代前半までは税務会計の専門家として活動していましたが、今はもっと<span className="font-bold text-green-600">経営の本質に踏み込んだ支援</span>に力を注いでいます。
                  </p>
                  <p className="font-medium">
                    中小企業の経営者は本当に大変です。売上を上げながら、お金の管理をして、人の問題も解決しなければならない。一人で全部背負っているのが現実です。
                  </p>
                  <p className="font-medium">
                    だからこそ、私は<span className="font-bold text-blue-600">「社長の横に立つパートナー」</span>として、一緒に考え、一緒に解決していくスタイルを大切にしています。上から目線で指導するのではなく、<span className="font-bold text-green-600">経営幹部のような立場</span>で経営判断をサポートします。
                  </p>
                  <p className="font-medium">
                    これまで多くの中小企業を支援してきた経験から、<span className="font-bold text-orange-600">シンプルで強力な経営の原理原則</span>こそが最も効果的だと確信しています。小難しい理論ではなく、基本の徹底と継続。それが経営を変える力になります。
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Message Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 文章削除 */}
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            あなたはこんな経営の悩みを抱えていませんか？
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: "ri-time-line",
                text: "毎日、急ぎの対応に追われ、ゆっくり経営を考える時間がない"
              },
              {
                icon: "ri-money-dollar-circle-line",
                text: "お金の流れが漠然としていて先の見通しが立たない"
              },
              {
                icon: "ri-wallet-line",
                text: "利益は出ているのに、なぜか手元にお金が残らない"
              },
              {
                icon: "ri-building-line",
                text: "現場から抜け出さず、経営に集中できない"
              },
              {
                icon: "ri-team-line",
                text: "従業員との危機感のずれを感じる"
              },
              {
                icon: "ri-user-search-line",
                text: "経営を一緒に考えるパートナーがいない"
              }
            ].map((problem, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border-l-4 border-red-500 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <i className={`${problem.icon} text-red-500 text-3xl sm:text-4xl md:text-5xl w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 flex items-center justify-center flex-shrink-0`}></i>
                  <p className="text-gray-700 leading-relaxed font-medium text-sm sm:text-base md:text-lg">{problem.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cause Analysis Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4 sm:mb-6 tracking-tight leading-tight">
              これらの悩みの原因は、中小企業にありがちな<br className="hidden sm:block" />
              <span className="text-red-600 font-extrabold">「文鎮型組織」</span>にあります。
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-1 gap-8 sm:gap-10 md:gap-12">
            <div className="w-full">
              <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-start">
                <div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 border-b-4 border-blue-500 pb-2">「文鎮型組織」とは</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base md:text-lg font-medium">
                    中小企業には、中堅企業以上に必ず存在する、守りの要である「部長」がおらず、社長が一人で「売上」・「お金」・「人」・「管理」等を経営の全てを兼務している状態のことをいいます。
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base md:text-lg font-medium">
                    この状態が、<span className="font-bold text-red-600">「情報の混乱」</span>や<span className="font-bold text-red-600">「指示のやりっぱなし」</span>を繰り返します。
                  </p>
                  
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 border-b-4 border-green-500 pb-2">脳のタイプ</h3>
                  <p className="text-gray-700 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base md:text-lg font-medium">
                    また、人は右脳タイプ左脳タイプと言われるように、脳のタイプがあり<span className="font-bold text-blue-600">「攻め」「守り」「管理」</span>などの得意分野が違います。
                  </p>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    社員が<span className="font-bold text-orange-600 text-lg sm:text-xl">１０人を超える</span>と社長ひとりで「売上」「お金」「人」をすべて管理する限界をこえてきます。
                  </p>
                </div>
                <div className="flex justify-center items-start">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/0b35755bdae877b9d1edf9825e019d07.jpg"
                    alt="文鎮型組織の図解"
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-blue-50">
        <div className="mb-8 sm:mb-10 md:mb-12">
          <img 
            src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/ddd3fda797e4baf13c403e7a54b4ede2.png"
            alt="社外財務部長サービス"
            className="w-full shadow-lg"
          />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4 sm:mb-6 tracking-tight">
              <span className="text-blue-600">社外財務部長</span>の活用のご提案
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto font-medium">
              上記の内容をお読みいただき、もしかしたらと感じられた方には、<br className="hidden sm:block" />
              <span className="font-bold text-blue-600">「社外財務部長」サービス</span>の活用で<br className="hidden sm:block" />
              <span className="font-bold text-green-600 text-lg sm:text-xl md:text-2xl">問題を改善できる可能性があります。</span>
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center mb-8 sm:mb-10 md:mb-12">
            <div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 border-b-4 border-orange-500 pb-2">社外財務部長とは</h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                社外財務部長とは、企業幹部の役割担う社外ＣＦＯ（財務部長）のことであり、経営に関する悩みを本質的に解決するために経営者と一緒に考え、実行のサポートを行うサービスです。中小企業の一番の悩みである<span className="font-bold text-green-600">『お金』</span>に専門性を置きつつ、企業幹部の役割を、<span className="font-bold text-orange-600">パートさん一人を雇うぐらいの投資額</span>で担います。
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/d639affe562e1f1dce326e3d4102edb1.png"
                alt="社外財務部長の概念図"
                className="w-2/3 max-w-sm rounded-lg shadow-lg"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 border-t-8 border-blue-500">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              社長の横に立って二人三脚で経営を支える<br className="hidden sm:block" />
              <span className="text-blue-600">パートナー型コンサルティング</span>
            </h3>
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
              <div>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                  一般的な士業やコンサルタントとは違う、士業・コンサルタント＝先生（やり方を教える、考える）という考え方ではありません。専門的な知識をアドバイスに重点を置くのではなく、<span className="font-bold text-blue-600">社長の横に立って一緒に</span>社長のお考えごと（人・売上・お金・情報）を解決するために一緒に考える<span className="font-bold text-green-600">経営幹部のような立ち位置</span>で経営判断をサポートするのが特徴です。
                </p>
              </div>
              <div className="flex justify-center">
                <img 
                  src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/a95b6aaecdefea9da7eb6f9aedba6fcb.png"
                  alt="パートナー型コンサルティングの概念図"
                  className="w-2/3 max-w-sm rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Why It Works Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            <span className="text-blue-600">社外財務部長</span>で経営が変わる理由
          </h2>
          
          <div className="mb-8 sm:mb-10 md:mb-12">
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-700 leading-relaxed sm:leading-relaxed md:leading-relaxed lg:leading-relaxed font-medium text-center max-w-full mx-auto">
              中小企業に必要なのは、小難しい経営理論ではなく、<br className="hidden sm:block" />
              <span className="bg-yellow-300 px-2 py-1 rounded font-bold text-gray-900">シンプルで強力な経営の原理原則の実践</span>です。<br className="hidden sm:block" />
              特に、基本(PDCAサイクル)の徹底と継続こそが最強であると考えています。
            </p>
          </div>

          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 border-b-4 border-green-500 pb-2">私たちの次の成果を続けます。</h3>
            <p className="text-gray-700 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base md:text-lg font-medium">
              この考えをベースに、経営会議や社員面談、社員教育について、次の成果を考えます。
            </p>
            
            <div className="space-y-3 sm:space-y-4">
              {[
                "PDCAサイクルの確立：経営の悪循環を修正し、経営フローを最適化",
                "脱ドンブリ経営：勘と経験に頼らず、予実管理・KPI・意思決定の基準を整備。根拠ある判断と速やかな打ち手の体制を築きます。",
                "お金の流れを見える化：売上・原価・固定費・資金繰りを可視化。どこにお金が生まれ・消えているかを明確にします。",
                "社員との危機感の共有：社長と社員の温度差を解消し、一体感を生む"
              ].map((result, index) => (
                <div key={index} className="flex items-start space-x-2 sm:space-x-3 bg-green-50 p-3 sm:p-4 rounded-lg border-l-4 border-green-500">
                  <i className="ri-check-line text-green-500 text-xl sm:text-2xl mt-1 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center font-bold flex-shrink-0"></i>
                  <p className="text-gray-700 font-semibold text-sm sm:text-base md:text-lg">{result}</p>
                </div>
              ))}
            </div>
            
            <p className="text-gray-700 leading-relaxed mt-4 sm:mt-6 text-sm sm:text-base md:text-lg font-medium">
              その結果として、多くの企業で<span className="font-bold text-blue-600">売上や利益の向上</span>、<span className="font-bold text-green-600">資金繰りの改善</span>といった成果が生まれています。
            </p>
          </div>
        </div>
      </section>

      {/* Three Assurances Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            社外財務部長活用の<span className="text-blue-600">３つの安心</span>
          </h2>
          
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-l-8 border-blue-500 hover:shadow-2xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">安心１：税理士の国家資格保有者が担当します</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    対応するコンサルタントは、信頼できる<span className="font-bold text-blue-600">「数字」と「お金」の専門家</span>でもある税理士行政書士資格を有します。コンサル会社出身の経験を活かして、通常の士業業務とは全く異なるアプローチで、<span className="font-bold text-green-600">「お金」「売上」「社員」</span>という経営の本質的なお悩みを解決の支援を致します。
                  </p>
                </div>
                <div className="lg:col-span-1 flex justify-center">
                  <i className="ri-award-line text-blue-500 text-5xl sm:text-6xl md:text-7xl lg:text-8xl w-16 sm:w-18 md:w-20 h-16 sm:h-18 md:h-20 flex items-center justify-center"></i>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-l-8 border-green-500 hover:shadow-2xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">安心２：コンサルトタント自身も独立系コンサルタントとして、約10年自らも実践</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    <span className="font-bold text-green-600">20年以上の中小企業支援</span>を通じて得たノウハウを通じて<span className="font-bold text-blue-600">「お金」と「人」</span>のお困りごとを解決の支援を致します。
                  </p>
                </div>
                <div className="lg:col-span-1 flex justify-center">
                  <i className="ri-user-star-line text-green-500 text-5xl sm:text-6xl md:text-7xl lg:text-8xl w-16 sm:w-18 md:w-20 h-16 sm:h-18 md:h-20 flex items-center justify-center"></i>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-l-8 border-orange-500 hover:shadow-2xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">安心３：２カ月のお試し顧問と６カ月の更新制</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    契約前に、<span className="font-bold text-orange-600">お試し期間が２か月（正規報酬額の６０％）</span>がありますので、その間にご判断頂くことが可能です。また、<span className="font-bold text-blue-600">６か月の更新制</span>となっていますので安心です。
                  </p>
                </div>
                <div className="lg:col-span-1 flex justify-center">
                  <i className="ri-calendar-check-line text-orange-500 text-5xl sm:text-6xl md:text-7xl lg:text-8xl w-16 sm:w-18 md:w-20 h-16 sm:h-18 md:h-20 flex items-center justify-center"></i>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            <span className="text-blue-600">料金体系</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            <div className="bg-blue-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-t-4 border-blue-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-4 sm:mb-6">
                <i className="ri-calendar-line text-blue-500 text-4xl sm:text-5xl md:text-6xl w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4"></i>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">毎月１回訪問</h3>
                <p className="text-base sm:text-lg text-gray-600 font-medium">1回４時間</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-blue-600 mb-2">8万円～</div>
                <p className="text-base sm:text-lg text-gray-600 font-medium">1か月（税別）</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-t-4 border-green-500 hover:shadow-2xl transition-shadow">
              <div className="text-center mb-4 sm:mb-6">
                <i className="ri-calendar-2-line text-green-500 text-4xl sm:text-5xl md:text-6xl w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4"></i>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">毎月２回訪問</h3>
                <p className="text-base sm:text-lg text-gray-600 font-medium">1回４時間</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-green-600 mb-2">12万円～</div>
                <p className="text-base sm:text-lg text-gray-600 font-medium">1か月（税別）</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-base sm:text-lg text-gray-700 font-medium">
              ※ 企業規模や課題の内容により料金が変動する場合があります。詳細はお問い合わせください。
            </p>
          </div>
        </div>
      </section>

      {/* Economic Benefits Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">
            社外財務部長活用の<span className="text-green-600">５つの経済的メリット</span>
          </h2>
          
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl border-l-8 border-blue-500 hover:shadow-2xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">① 社長の経営集中による経済効果</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    社長の頭の中から<span className="font-bold text-red-600">「お金の流れが漠然としていて先の見通しがたたない」</span>を解消し、<span className="font-bold text-blue-600">「社長と社員の立場の違いが生む危機感のズレ」</span>を改善することにより、<span className="font-bold text-blue-600">本業に集中</span>することが可能となります。
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <img 
                    src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/8d1c2c2010ce8702b0d1d1000fc21c3f.webp"
                    alt="社長の経営集中"
                    className="w-full h-24 sm:h-28 md:h-32 rounded-lg shadow-md object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 sm:p-6 md:p-8 rounded-lg border-l-8 border-green-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">②人件費削減</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    通常、新たな留意事項に部長雄雇用となると、最低でも<span className="font-bold text-red-600">月に４０万～５０万円程度の給料</span>が必要となります。当サービスであれば、本当に必要な業務に重点を置くことにより、<span className="font-bold text-green-600">パートさんを用いたような金額</span>で、経営を支える人財を仲間にすることができます。
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <img 
                    src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/fb8f3447190d1425f4ea42e3350f937b.webp"
                    alt="人件費削減"
                    className="w-full h-24 sm:h-28 md:h-32 rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 sm:p-6 md:p-8 rounded-lg border-l-8 border-purple-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">③離職による教育投資ロスゼロ</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    その為、従業員である限り経営者が意図しないタイミングでの<span className="font-bold text-red-600">退職リスク</span>を恐れることになります。 教育して投資をした後の人材の退職は中小企業にとって最近大きな損失となります。 <span className="font-bold text-purple-600">『社外部長』サービス</span>には社長が意図しないタイミングでの退職はありません。
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <img 
                    src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/95e64783b1dd7d0090439fd260f48381.webp"
                    alt="離職リスクゼロ"
                    className="w-full h-24 sm:h-28 md:h-32 rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 sm:p-6 md:p-8 rounded-lg border-l-8 border-yellow-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">④無駄な経費や投資の削減</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    一人で考えている間はゆっくりな事業計画の策定時に、一人の各経費や投資効果の見直しを行うことで、<span className="font-bold text-yellow-600">無駄な経費を削減</span>することで利益を行います。
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <img 
                    src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/133d4314a297be8809a34d657497adc5.jpeg"
                    alt="無駄な経費削減"
                    className="w-full h-24 sm:h-28 md:h-32 rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="bg-red-50 p-4 sm:p-6 md:p-8 rounded-lg border-l-8 border-red-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8 items-center">
                <div className="lg:col-span-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">⑤幹部育成時間の短縮</h3>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                    経営者感覚の教育は非常に時間がかかります。なぜなら、通常従業員には独立開業の経験がないからです。当サービスを利用すると、<span className="font-bold text-red-600">数年間の育成期間⇒２～３回程度の打合せ</span>で幹部人材を抱えたのと同じような効果を感じることができます。
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <img 
                    src="https://static.readdy.ai/image/be4bc20f7117d3752f892228118b29ca/65e26868fa714d4abe84dc0c3a9fe9fc.jpeg"
                    alt="幹部育成時間短縮"
                    className="w-full h-24 sm:h-28 md:h-32 rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center text-gray-900 mb-8 sm:mb-10 md:mb-12 tracking-tight">よくあるご質問</h2>
          
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-blue-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-l-4 border-blue-500">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-start">
                <span className="bg-blue-500 text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base md:text-lg font-black mr-3 sm:mr-4 flex-shrink-0">Q1</span>
                どのような企業が対象ですか？
              </h3>
              <div className="ml-10 sm:ml-12 md:ml-16">
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                  <span className="font-bold text-blue-600">従業員30人未満の中小企業</span>が主な対象です。特に、社長が一人で「売上」「お金」「人」のすべてを管理している「文鎮型組織」の企業様に最適です。業種は問いません。
                </p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-l-4 border-green-500">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-start">
                <span className="bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base md:text-lg font-black mr-3 sm:mr-4 flex-shrink-0">Q2</span>
                どのくらいの期間で効果が出ますか？
              </h3>
              <div className="ml-10 sm:ml-12 md:ml-16">
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                  <span className="font-bold text-green-600">2〜3ヶ月程度</span>で経営の見える化や基本的な仕組みづくりの効果を実感していただけます。売上や利益の向上については、企業の状況により異なりますが、多くの場合<span className="font-bold text-blue-600">6ヶ月〜1年程度</span>で成果が現れています。
                </p>
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-l-4 border-orange-500">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-start">
                <span className="bg-orange-500 text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base md:text-lg font-black mr-3 sm:mr-4 flex-shrink-0">Q3</span>
                どのようなサポートを受けられますか？
              </h3>
              <div className="ml-10 sm:ml-12 md:ml-16">
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                  <span className="font-bold text-orange-600">月１〜２回の定期面談</span>、経営会議への参加、予実管理の仕組み構築、社員面談のサポート、資金繰り管理、KPI設定と運用支援などを行います。また、緊急時には随時相談も可能です。
                </p>
              </div>
            </div>

            <div className="bg-purple-50 p-4 sm:p-6 md:p-8 rounded-lg shadow-lg border-l-4 border-purple-500">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-start">
                <span className="bg-purple-500 text-white px-2 sm:px-3 py-1 rounded-full text-sm sm:text-base md:text-lg font-black mr-3 sm:mr-4 flex-shrink-0">Q4</span>
                料金はどのくらいですか？
              </h3>
              <div className="ml-10 sm:ml-12 md:ml-16">
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg font-medium">
                  <span className="font-bold text-purple-600">お試し期間（2ヶ月）は正規料金の60%、その後は月額制となります。</span><br/>
                  詳細な料金については、初回相談時に事業規模や課題に応じてご提案させていただきます。<span className="font-bold text-blue-600">パートさん一人を雇う程度の投資</span>で、経営幹部レベルの支援を受けることができます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6 tracking-tight">
            経営の悩みを一人で抱え込まず、<br className="hidden sm:block" />
            <span className="text-blue-300">一緒に解決していきませんか？</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8 leading-relaxed font-medium">
            まずは無料相談で、あなたの経営課題をお聞かせください。<br className="hidden sm:block" />
            社外財務部長として、どのようなサポートができるかご提案いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a 
              href="/start-now" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg md:text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              📩 無料相談のお申し込み
            </a>
            <a 
              href="/trial" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg md:text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              2ヶ月試し顧問について
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4 sm:mb-6 tracking-tight">お問い合わせフォーム</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed font-medium">
              経営に関するお悩みやご質問がございましたら、お気軽にお問い合わせください。<br className="hidden sm:block" />
              24時間以内にご返信いたします。
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 border-t-8 border-blue-500">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="山田太郎"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-bold text-gray-700 mb-2">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="092-841-5550"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="経営に関するお悩みやご質問をお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-800 font-medium">
                    お問い合わせありがとうございます。24時間以内にご返信いたします。
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-red-800 font-medium">
                    送信に失敗しました。しばらく時間をおいて再度お試しください。
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-10 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">社外財務部長サービス</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                中小企業経営者の横に立って、二人三脚で経営を支えるパートナー型コンサルティングサービスです。
              </p>
            </div>
            <div>
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">サービス内容</h4>
              <ul className="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base">
                <li>• 経営会議サポート</li>
                <li>• 予実管理の仕組み構築</li>
                <li>• 資金繰り管理</li>
                <li>• 社員面談サポート</li>
                <li>• KPI設定と運用支援</li>
              </ul>
            </div>
            <div>
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">お問い合わせ</h4>
              <div className="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base">
                <p>📧 メール: shikazei@hi2.enjoy.ne.jp</p>
                <p className="text-2xl sm:text-3xl font-black text-blue-600 mb-2 sm:mb-4">092-841-5550</p>
                <p>📞 電話: 092-841-5550</p>
                <p>🕒 受付時間: 平日 9:00-18:00</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 社外財務部長サービス. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;